import React from 'react';

/**
 * A Custom Hook that filters React children based on their types.
 *
 * @function filterChildrenOnType
 * @param {Array<React.ElementType>} childTypes - An Array of React component types to filter by.
 * @param {React.ReactNode} children - The React children to be filtered.
 * @returns {Array} An Array containing elements for each specified type and an additional element for non-matching children:
 *
 *   - An Array of children that match the specified types, in the same order as `childTypes`.
 *   - An Array of children that do not match any of the specified types.
 * @example
 * const [buttons, labels, otherChildren] = useChildrenOfType([Button, Label], props.children);
 * // Use the `buttons`, `labels`, and `otherChildren` Arrays for rendering or other purposes.
 */
export default function filterChildrenOnType(childTypes, children) {
  const allChildren = React.Children.toArray(children);

  const initialResult = childTypes.map(() => []).concat([[]]);

  return allChildren.reduce((accumulator, child) => {
    const index = childTypes.findIndex(childType => child.type === childType);

    if (index >= 0) {
      accumulator[index].push(child);
    } else {
      accumulator[childTypes.length].push(child);
    }

    return accumulator;
  }, initialResult);
}
